import Image from "next/image"
import Link from "next/link"
import { ArrowRight, BadgeCheck, CalendarDays, Check, Mail, MapPin, Phone, ShieldCheck, Sparkles, Star } from "lucide-react"

import { MotionDiv, MotionList, MotionSection, blurIn, fadeInLeft, fadeInRight, scaleIn } from "@/components/motion"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { cn } from "@/lib/utils"
import { CountUpNumber, LiftCard, MagneticCta } from "@/components/motion-primitives"

export type PricingPlan = {
  name: string
  description?: string
  price: string
  period?: string
  features: string[]
  cta: { label: string; href: string }
  highlighted?: boolean
  badge?: string
}

export type PricingSectionProps = {
  eyebrow?: string
  title: string
  description?: string
  plans: PricingPlan[]
  footnote?: string
  className?: string
}

export type TeamMember = {
  name: string
  role: string
  bio?: string
  image?: { src: string; alt: string }
}

export type TeamAboutSectionProps = {
  eyebrow?: string
  title: string
  description?: string
  members: TeamMember[]
  values?: { title: string; description: string }[]
  className?: string
}

export type ContactLocation = {
  label: string
  address?: string
  phone?: string
  email?: string
  hours?: string
}

export type ContactSectionProps = {
  eyebrow?: string
  title: string
  description?: string
  locations?: ContactLocation[]
  channels?: { label: string; value: string; href: string }[]
  cta?: { label: string; href: string }
  className?: string
}

export type GalleryItem = {
  src: string
  alt: string
  caption?: string
}

export type MediaGallerySectionProps = {
  eyebrow?: string
  title: string
  description?: string
  items: GalleryItem[]
  className?: string
}

export type ProductSpotlightSectionProps = {
  eyebrow?: string
  title: string
  description?: string
  image: { src: string; alt: string }
  price?: string
  rating?: string
  features?: string[]
  primaryCta: { label: string; href: string }
  secondaryCta?: { label: string; href: string }
  className?: string
}

export type LegalUtilitySectionProps = {
  eyebrow?: string
  title: string
  description: string
  updatedAt?: string
  anchors?: { label: string; href: string }[]
  cta?: { label: string; href: string }
  className?: string
}

/** Server-first pricing grid with plan cards ready for Product and Offer schema. */
export function PricingSection({ eyebrow, title, description, plans, footnote, className }: PricingSectionProps) {
  return (
    <MotionSection className={cn("bg-background py-24 sm:py-32", className)}>
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <MotionDiv variants={fadeInLeft} className="mx-auto max-w-3xl text-center">
          {eyebrow && <Badge variant="secondary" className="mb-5 rounded-full px-4 py-2 text-primary">{eyebrow}</Badge>}
          <h2 className="font-heading text-4xl font-semibold tracking-tight text-foreground sm:text-6xl">{title}</h2>
          {description && <p className="mt-5 text-lg leading-8 text-muted-foreground">{description}</p>}
        </MotionDiv>

        <MotionList className="mt-12 grid gap-5 lg:grid-cols-3">
          {plans.map((plan) => (
            <MotionDiv key={plan.name} variants={scaleIn} className="h-full">
              <LiftCard lift={6} scale={plan.highlighted ? 1.03 : 1.02}>
                <Card className={cn("relative flex h-full flex-col rounded-panel border-border/70 bg-card p-7 shadow-[var(--shadow-card)] transition-shadow hover:shadow-elegant", plan.highlighted && "border-primary/40 bg-primary/6 shadow-[0_28px_90px_hsl(var(--primary)/0.14)]")}>
                  {plan.badge && (
                    <span className="mb-6 inline-flex w-fit items-center gap-2 rounded-full bg-primary px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-primary-foreground">
                      <Sparkles className="size-3.5" />
                      {plan.badge}
                    </span>
                  )}
                  <div>
                    <h3 className="font-heading text-2xl font-semibold tracking-tight text-foreground">{plan.name}</h3>
                    {plan.description && <p className="mt-3 min-h-14 text-sm leading-6 text-muted-foreground">{plan.description}</p>}
                    <p className="mt-7 flex items-end gap-2">
                      <span className="font-heading text-5xl font-semibold tracking-tight text-foreground">{plan.price}</span>
                      {plan.period && <span className="pb-2 text-sm text-muted-foreground">/{plan.period}</span>}
                    </p>
                  </div>
                  <ul className="mt-7 flex-1 space-y-3">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex gap-3 text-sm leading-6 text-muted-foreground">
                        <Check className="mt-0.5 size-4 shrink-0 text-primary" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Button asChild size="lg" className="mt-8 w-full rounded-button transition-transform duration-300 hover:scale-[1.03]" variant={plan.highlighted ? "default" : "outline"}>
                    <Link href={plan.cta.href}>{plan.cta.label}</Link>
                  </Button>
                </Card>
              </LiftCard>
            </MotionDiv>
          ))}
        </MotionList>
        {footnote && <p className="mx-auto mt-8 max-w-2xl text-center text-sm leading-6 text-muted-foreground">{footnote}</p>}
      </div>
    </MotionSection>
  )
}

/** Static about/team block for Person and Organization schema surfaces. */
export function TeamAboutSection({ eyebrow, title, description, members, values = [], className }: TeamAboutSectionProps) {
  return (
    <MotionSection className={cn("bg-muted/44 py-24 sm:py-32", className)}>
      <div className="mx-auto grid w-full max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[0.82fr_1.18fr] lg:px-8">
        <MotionDiv variants={fadeInLeft} className="lg:sticky lg:top-24 lg:self-start">
          {eyebrow && <p className="mb-5 text-sm font-semibold uppercase tracking-[0.24em] text-primary">{eyebrow}</p>}
          <h2 className="font-heading text-4xl font-semibold tracking-tight text-foreground sm:text-6xl">{title}</h2>
          {description && <p className="mt-5 text-lg leading-8 text-muted-foreground">{description}</p>}
          {values.length > 0 && (
            <div className="mt-8 grid gap-3">
              {values.map((value) => (
                <div key={value.title} className="rounded-card border border-border/70 bg-background p-5 shadow-sm">
                  <h3 className="font-heading text-lg font-semibold text-foreground">{value.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-muted-foreground">{value.description}</p>
                </div>
              ))}
            </div>
          )}
        </MotionDiv>
        <MotionList className="grid gap-5 sm:grid-cols-2">
          {members.map((member) => (
            <MotionDiv key={member.name} variants={blurIn}>
              <LiftCard lift={6} scale={1.02}>
                <Card className="group h-full overflow-hidden rounded-panel border-border/70 bg-card shadow-[var(--shadow-card)] transition-shadow hover:shadow-elegant">
                  {member.image && (
                    <div className="relative h-72 overflow-hidden bg-muted">
                      <Image
                        src={member.image.src}
                        alt={member.image.alt}
                        fill
                        sizes="(min-width: 768px) 33vw, 100vw"
                        className="object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <h3 className="font-heading text-2xl font-semibold tracking-tight text-foreground">{member.name}</h3>
                    <p className="mt-1 text-sm font-medium text-primary">{member.role}</p>
                    {member.bio && <p className="mt-4 leading-7 text-muted-foreground">{member.bio}</p>}
                  </div>
                </Card>
              </LiftCard>
            </MotionDiv>
          ))}
        </MotionList>
      </div>
    </MotionSection>
  )
}

/** Contact shell for service pages, local businesses, and multi-location sites. */
export function ContactSection({ eyebrow, title, description, locations = [], channels = [], cta, className }: ContactSectionProps) {
  return (
    <MotionSection className={cn("relative isolate overflow-hidden bg-foreground py-24 text-background sm:py-32", className)}>
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_20%_15%,hsl(var(--primary)/0.24),transparent_32%),radial-gradient(circle_at_82%_72%,hsl(var(--accent)/0.18),transparent_28%)]" />
      <div className="mx-auto grid w-full max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[0.88fr_1.12fr] lg:px-8">
        <MotionDiv variants={fadeInLeft}>
          {eyebrow && <Badge className="mb-5 rounded-full border-background/14 bg-background/10 px-4 py-2 text-background">{eyebrow}</Badge>}
          <h2 className="font-heading text-4xl font-semibold tracking-tight sm:text-6xl">{title}</h2>
          {description && <p className="mt-5 text-lg leading-8 text-background/70">{description}</p>}
          {cta && (
            <Button asChild size="xl" className="mt-9 rounded-full bg-background text-foreground hover:bg-background/90">
              <Link href={cta.href}>{cta.label}</Link>
            </Button>
          )}
        </MotionDiv>
        <MotionList className="grid gap-4">
          {locations.map((location) => (
            <MotionDiv key={location.label} variants={scaleIn}>
              <Card className="rounded-panel border-background/10 bg-background/10 p-6 text-background shadow-none backdrop-blur-xl">
                <div className="mb-5 flex items-center gap-3">
                  <span className="flex size-11 items-center justify-center rounded-card bg-accent text-accent-foreground"><MapPin className="size-5" /></span>
                  <h3 className="font-heading text-2xl font-semibold tracking-tight">{location.label}</h3>
                </div>
                <div className="grid gap-3 text-sm leading-6 text-background/70 sm:grid-cols-2">
                  {location.address && <p>{location.address}</p>}
                  {location.hours && <p>{location.hours}</p>}
                  {location.phone && <p className="inline-flex items-center gap-2"><Phone className="size-4" /> {location.phone}</p>}
                  {location.email && <p className="inline-flex items-center gap-2"><Mail className="size-4" /> {location.email}</p>}
                </div>
              </Card>
            </MotionDiv>
          ))}
          {channels.length > 0 && (
            <div className="grid gap-3 sm:grid-cols-2">
              {channels.map((channel) => (
                <Link key={channel.href} href={channel.href} className="rounded-card border border-background/10 bg-background/8 p-5 text-sm text-background/76 transition-colors hover:bg-background/14">
                  <span className="block font-semibold text-background">{channel.label}</span>
                  <span className="mt-1 block">{channel.value}</span>
                </Link>
              ))}
            </div>
          )}
        </MotionList>
      </div>
    </MotionSection>
  )
}

/** Server-rendered gallery grid; lightbox/carousel variants should be small client islands. */
export function MediaGallerySection({ eyebrow, title, description, items, className }: MediaGallerySectionProps) {
  return (
    <MotionSection className={cn("bg-background py-24 sm:py-32", className)}>
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <MotionDiv variants={fadeInLeft} className="max-w-3xl">
          {eyebrow && <p className="mb-5 text-sm font-semibold uppercase tracking-[0.24em] text-primary">{eyebrow}</p>}
          <h2 className="font-heading text-4xl font-semibold tracking-tight text-foreground sm:text-6xl">{title}</h2>
          {description && <p className="mt-5 text-lg leading-8 text-muted-foreground">{description}</p>}
        </MotionDiv>
        <MotionList className="mt-12 grid auto-rows-[18rem] gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((item, index) => (
            <MotionDiv key={item.src} variants={blurIn} className={cn("group relative overflow-hidden rounded-panel bg-muted shadow-[var(--shadow-card)]", index === 0 && "sm:col-span-2 sm:row-span-2", index === 3 && "lg:col-span-2")}>
              <Image src={item.src} alt={item.alt} fill sizes="(min-width: 1024px) 33vw, 100vw" className="object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground/62 via-transparent to-transparent opacity-80" />
              {item.caption && <p className="absolute inset-x-5 bottom-5 text-sm font-medium text-background">{item.caption}</p>}
            </MotionDiv>
          ))}
        </MotionList>
      </div>
    </MotionSection>
  )
}

/** Commerce-ready product spotlight with server-rendered product and offer details. */
export function ProductSpotlightSection({ eyebrow, title, description, image, price, rating, features = [], primaryCta, secondaryCta, className }: ProductSpotlightSectionProps) {
  return (
    <MotionSection className={cn("bg-muted/44 py-24 sm:py-32", className)}>
      <div className="mx-auto grid w-full max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-[1.02fr_0.98fr] lg:px-8">
        <MotionDiv variants={fadeInLeft} className="relative min-h-[34rem] overflow-hidden rounded-panel bg-muted shadow-[0_28px_90px_hsl(var(--foreground)/0.16)]">
          <Image src={image.src} alt={image.alt} fill sizes="(min-width: 1024px) 50vw, 100vw" className="object-cover" />
        </MotionDiv>
        <MotionDiv variants={fadeInRight}>
          {eyebrow && <Badge variant="secondary" className="mb-5 rounded-full px-4 py-2 text-primary">{eyebrow}</Badge>}
          <h2 className="font-heading text-4xl font-semibold tracking-tight text-foreground sm:text-6xl">{title}</h2>
          <p className="mt-5 text-lg leading-8 text-muted-foreground">{description}</p>
          <div className="mt-7 flex flex-wrap items-center gap-4">
            {price && <p className="font-heading text-4xl font-semibold tracking-tight text-foreground">{price}</p>}
            {rating && <p className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-card px-4 py-2 text-sm text-muted-foreground"><Star className="size-4 fill-primary text-primary" /> {rating}</p>}
          </div>
          {features.length > 0 && (
            <ul className="mt-8 grid gap-3 sm:grid-cols-2">
              {features.map((feature) => (
                <li key={feature} className="flex gap-3 text-sm leading-6 text-muted-foreground"><BadgeCheck className="mt-0.5 size-4 shrink-0 text-primary" />{feature}</li>
              ))}
            </ul>
          )}
          <div className="mt-9 flex flex-col gap-3 sm:flex-row">
            <Button asChild size="xl" className="group rounded-full"><Link href={primaryCta.href}>{primaryCta.label}<ArrowRight className="transition-transform duration-300 group-hover:translate-x-1" /></Link></Button>
            {secondaryCta && <Button asChild size="xl" variant="outline" className="rounded-full"><Link href={secondaryCta.href}>{secondaryCta.label}</Link></Button>}
          </div>
        </MotionDiv>
      </div>
    </MotionSection>
  )
}

/** Server-rendered utility hero for legal, 404, maintenance, and coming-soon pages. */
export function LegalUtilitySection({ eyebrow, title, description, updatedAt, anchors = [], cta, className }: LegalUtilitySectionProps) {
  return (
    <section className={cn("bg-background py-20 sm:py-28", className)}>
      <div className="mx-auto grid w-full max-w-5xl gap-10 px-4 sm:px-6 lg:grid-cols-[0.72fr_0.28fr] lg:px-8">
        <div>
          {eyebrow && <p className="mb-5 text-sm font-semibold uppercase tracking-[0.24em] text-primary">{eyebrow}</p>}
          <h1 className="font-heading text-5xl font-semibold leading-[0.92] tracking-tight text-foreground sm:text-7xl">{title}</h1>
          <p className="mt-6 text-lg leading-8 text-muted-foreground">{description}</p>
          {updatedAt && <p className="mt-6 inline-flex items-center gap-2 text-sm text-muted-foreground"><CalendarDays className="size-4" /> Last updated {updatedAt}</p>}
          {cta && <Button asChild size="lg" className="mt-8 rounded-full"><Link href={cta.href}>{cta.label}</Link></Button>}
        </div>
        {anchors.length > 0 && (
          <nav aria-label="Page sections" className="rounded-panel border border-border/70 bg-card p-5 shadow-sm lg:self-start">
            <p className="mb-4 inline-flex items-center gap-2 text-sm font-semibold text-foreground"><ShieldCheck className="size-4 text-primary" /> On this page</p>
            <div className="grid gap-2 text-sm text-muted-foreground">
              {anchors.map((anchor) => <Link key={anchor.href} href={anchor.href} className="rounded-xl px-3 py-2 transition-colors hover:bg-muted hover:text-foreground">{anchor.label}</Link>)}
            </div>
          </nav>
        )}
      </div>
    </section>
  )
}